import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { getSeedNameFromTitle } from '../../../constants/predefinedActivities';
import { CHATBOT_STATUS } from '../../../constants/status';
import { useChatbotContext, useChatStreaming } from '../../../hooks/ChatbotContext';
import useChatbotPracticeView from '../../../hooks/useChatbotPracticeView';
import ModelNameEnum, { getModelLabel, getModelProvider } from '../../../models/ModelNameEnum';
import ActivityNameCombobox from '../ActivityNameCombobox/ActivityNameCombobox';
import './ChatDevTools.scss';

function ChatbotDevTools({
  chatContext,
  currentConversationId,
  systemPrompt,
  setSystemPrompt,
  model,
  setModel,
  temperature,
  setTemperature,
  maxTokens,
  setMaxTokens,
  onNewChat,
}) {
  const { mode, activityName, setActivityName } = useChatbotContext();
  const { conversationStatus } = useChatStreaming();

  // Always call the practice hook (React hooks must be called unconditionally)
  // but only use it when in practice mode
  const { generateActivity } = useChatbotPracticeView();
  const [isExpanded, setIsExpanded] = useState(false);
  const [notification, setNotification] = useState({ visible: false, message: '' });
  const [detectedContentGuid, setDetectedContentGuid] = useState('');

  // Use temperature and maxTokens from props (which will be coming from Redux state)

  const textareaRef = useRef(null);

  // Detect which GUID would be used as the contentGuid
  useEffect(() => {
    if (chatContext) {
      let contentGuid = '';

      // Look for different types of GUIDs in order of priority
      if (chatContext.contentGuid) {
        contentGuid = chatContext.contentGuid;
      } else if (chatContext.lessonGuid) {
        contentGuid = chatContext.lessonGuid;
      } else if (chatContext.programGuid) {
        contentGuid = chatContext.programGuid;
      } else if (chatContext.assessmentGuid) {
        contentGuid = chatContext.assessmentGuid;
      } else if (chatContext.resourceGuid) {
        contentGuid = chatContext.resourceGuid;
      } else if (chatContext.unitGuid) {
        contentGuid = chatContext.unitGuid;
      } else if (chatContext.viewerGuid) {
        contentGuid = chatContext.viewerGuid;
      } else {
        // Use the first guid available in the object if none of the specific ones are found
        const firstGuidKey = Object.keys(chatContext).find((key) => key.toLowerCase().includes('guid'));
        if (firstGuidKey) {
          contentGuid = chatContext[firstGuidKey];
        }
      }

      setDetectedContentGuid(contentGuid);
    } else {
      setDetectedContentGuid('');
    }
  }, [chatContext]);

  // Hide notification after 3 seconds
  useEffect(() => {
    if (notification.visible) {
      const timer = setTimeout(() => {
        setNotification({ visible: false, message: '' });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [notification.visible]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const formatContextData = (data) => {
    return JSON.stringify(data, null, 2);
  };

  const getStatusColor = () => {
    switch (conversationStatus) {
      case CHATBOT_STATUS.CONNECTING:
        return '#f9a825'; // Yellow
      case CHATBOT_STATUS.STREAMING:
        return '#2e7d32'; // Green
      case CHATBOT_STATUS.COMPLETE:
        return '#1565c0'; // Blue
      case CHATBOT_STATUS.ERROR:
        return '#c62828'; // Red
      default:
        return '#9e9e9e'; // Gray
    }
  };

  const handleSystemPromptChange = (e) => {
    setSystemPrompt(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      saveSystemPrompt();
    }
  };

  const saveSystemPrompt = () => {
    // System prompt is already saved via state
    // Show success notification
    setNotification({
      visible: true,
      message: systemPrompt ? 'System prompt updated' : 'System prompt cleared',
    });

    if (textareaRef.current) {
      textareaRef.current.blur();
    }
  };

  const clearSystemPrompt = () => {
    setSystemPrompt('');
    setNotification({
      visible: true,
      message: 'System prompt cleared',
    });
  };

  const handleModelChange = (e) => {
    setModel(e.target.value);
    setNotification({
      visible: true,
      message: `Model changed to ${getModelLabel(e.target.value)}`,
    });
  };

  const handleActivityNameChange = (newActivityName) => {
    setActivityName(newActivityName);
  };

  const handleActivityUpdate = async () => {
    if (activityName && mode === 'practice' && generateActivity) {
      try {
        setNotification({
          visible: true,
          message: 'Generating activity...',
        });

        // Get seedName from activity title
        const seedName = getSeedNameFromTitle(activityName);

        // Generate new activity with seedName using the practice hook
        // This will automatically update the UI because it shares the same state
        await generateActivity(seedName);

        // Trigger new conversation (similar to clicking "Nuevo chat" button)
        if (onNewChat) {
          onNewChat();
        }

        setNotification({
          visible: true,
          message: `Activity updated to: ${activityName}`,
        });
      } catch (error) {
        setNotification({
          visible: true,
          message: `Failed to update activity: ${error.message}`,
        });
      }
    }
  };

  // Group models by provider
  const groupedModels = Object.values(ModelNameEnum).reduce((acc, modelName) => {
    const provider = getModelProvider(modelName);
    if (!acc[provider]) {
      acc[provider] = [];
    }
    acc[provider].push(modelName);
    return acc;
  }, {});

  return (
    <div className={`chat-dev-tools ${isExpanded ? 'chat-dev-tools--expanded' : ''}`}>
      <div className="chat-dev-tools__header" onClick={toggleExpanded}>
        <span className="chat-dev-tools__title">DevTools</span>
        <div className="chat-dev-tools__status">
          <span className="chat-dev-tools__status-label">Status:</span>
          <span className="chat-dev-tools__status-indicator" style={{ backgroundColor: getStatusColor() }}></span>
          <span className="chat-dev-tools__status-text">{conversationStatus}</span>
        </div>
        {systemPrompt && (
          <div className="chat-dev-tools__prompt-indicator">
            <span className="chat-dev-tools__status-label">Custom prompt:</span>
            <span className="chat-dev-tools__prompt-active">Active</span>
          </div>
        )}
        <span className="chat-dev-tools__toggle">{isExpanded ? '▼' : '▲'}</span>
      </div>
      {isExpanded && (
        <div className="chat-dev-tools__content">
          <div className="chat-dev-tools__section">
            <h3>AI Model Configuration</h3>
            <div className="chat-dev-tools__select-wrapper">
              <select className="chat-dev-tools__select" value={model} onChange={handleModelChange}>
                {Object.keys(groupedModels).map((provider) => (
                  <optgroup key={provider} label={provider}>
                    {groupedModels[provider].map((modelName) => (
                      <option key={modelName} value={modelName}>
                        {getModelLabel(modelName)}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div className="chat-dev-tools__help-text">Select the AI model to use for this conversation.</div>

            <div className="chat-dev-tools__slider-container">
              <label className="chat-dev-tools__slider-label">
                Temperature: {temperature}
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={temperature}
                  onChange={(e) => {
                    const newTemp = parseFloat(e.target.value);
                    setTemperature(newTemp);
                    setNotification({
                      visible: true,
                      message: `Temperature set to ${newTemp}`,
                    });
                  }}
                  className="chat-dev-tools__slider"
                />
              </label>
              <div className="chat-dev-tools__help-text">Controls randomness: lower values are more deterministic, higher values more creative.</div>
            </div>

            <div className="chat-dev-tools__input-group">
              <label className="chat-dev-tools__input-label">
                Max Tokens:
                <input
                  type="number"
                  min="100"
                  max="4000"
                  step="1"
                  value={maxTokens}
                  onChange={(e) => {
                    const newTokens = parseInt(e.target.value, 10);
                    if (!isNaN(newTokens) && newTokens >= 100 && newTokens <= 4000) {
                      setMaxTokens(newTokens);
                      setNotification({
                        visible: true,
                        message: `Max tokens set to ${newTokens}`,
                      });
                    }
                  }}
                  className="chat-dev-tools__number-input"
                />
              </label>
              <div className="chat-dev-tools__help-text">Maximum length of the generated response.</div>
            </div>
          </div>

          <div className="chat-dev-tools__section">
            <h3>System Prompt</h3>
            <div className="chat-dev-tools__input-group">
              <textarea
                ref={textareaRef}
                className="chat-dev-tools__textarea"
                value={systemPrompt}
                onChange={handleSystemPromptChange}
                onKeyDown={handleKeyDown}
                placeholder="Enter a system prompt to guide the AI's behavior..."
                rows={3}
              />
              <div className="chat-dev-tools__buttons">
                {systemPrompt && (
                  <button type="button" className="chat-dev-tools__clear-button" onClick={clearSystemPrompt} title="Clear system prompt">
                    ×
                  </button>
                )}
                <button type="button" className="chat-dev-tools__save-button" onClick={saveSystemPrompt} title="Save system prompt" disabled={!systemPrompt}>
                  Save
                </button>
              </div>
            </div>
            <div className="chat-dev-tools__help-text">
              The system prompt helps set the behavior and knowledge context for the AI assistant.
              <br />
              <small>Press Ctrl+Enter to apply changes.</small>
            </div>
          </div>

          <div className="chat-dev-tools__section">
            <h3>Connection Info</h3>
            <div className="chat-dev-tools__info-grid">
              <div className="chat-dev-tools__info-item">
                <span className="chat-dev-tools__info-label">Conversation ID:</span>
                <span className="chat-dev-tools__info-value">{currentConversationId || 'No active conversation'}</span>
              </div>
              <div className="chat-dev-tools__info-item">
                <span className="chat-dev-tools__info-label">Content GUID:</span>
                <span className="chat-dev-tools__info-value">
                  {detectedContentGuid ? (
                    <>
                      {detectedContentGuid}
                      <button
                        type="button"
                        className="chat-dev-tools__copy-button"
                        onClick={() => {
                          navigator.clipboard.writeText(detectedContentGuid);
                          setNotification({ visible: true, message: 'GUID copied to clipboard' });
                        }}
                        title="Copy GUID"
                      >
                        Copy
                      </button>
                    </>
                  ) : (
                    <span className="chat-dev-tools__warning">No content GUID detected! API calls may fail without a valid GUID.</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {mode === 'practice' && (
            <div className="chat-dev-tools__section">
              <h3>Activity Configuration</h3>
              <div className="chat-dev-tools__input-group">
                <label className="chat-dev-tools__input-label">
                  Activity:
                  <div className="chat-dev-tools__activity-input-wrapper">
                    <ActivityNameCombobox
                      value={activityName}
                      onChange={handleActivityNameChange}
                      placeholder="Select or enter activity name..."
                      className="chat-dev-tools__activity-combobox"
                    />
                    <button
                      type="button"
                      className="chat-dev-tools__refresh-button"
                      onClick={handleActivityUpdate}
                      disabled={!activityName}
                      title="Generate new activity"
                    >
                      ↻
                    </button>
                  </div>
                </label>
                <div className="chat-dev-tools__help-text">Select from predefined activities or enter a custom name.</div>
              </div>
            </div>
          )}

          <div className="chat-dev-tools__section">
            <h3>Context Data</h3>
            <pre className="chat-dev-tools__code">{formatContextData(chatContext)}</pre>
          </div>
        </div>
      )}

      {notification.visible && <div className="chat-dev-tools__notification">{notification.message}</div>}
    </div>
  );
}

ChatbotDevTools.propTypes = {
  chatContext: PropTypes.object,
  currentConversationId: PropTypes.string,
  systemPrompt: PropTypes.string,
  setSystemPrompt: PropTypes.func,
  model: PropTypes.string,
  setModel: PropTypes.func,
  temperature: PropTypes.number,
  setTemperature: PropTypes.func,
  maxTokens: PropTypes.number,
  setMaxTokens: PropTypes.func,
  onNewChat: PropTypes.func,
};

ChatbotDevTools.defaultProps = {
  chatContext: {},
  currentConversationId: '',
  systemPrompt: '',
  setSystemPrompt: () => {},
  model: ModelNameEnum.CLAUDE_3_7_SONNET,
  setModel: () => {},
  temperature: 0.7,
  setTemperature: () => {},
  maxTokens: 2048,
  setMaxTokens: () => {},
  onNewChat: () => {},
};

export default ChatbotDevTools;
